import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const emptyForm = {
  audience: "",
  goal: "",
  offer: "",
  painPoint: "",
  desiredOutcome: "",
  angle: "",
  callToAction: "",
  tone: "Persuasive",
  keywords: "",
  brandVoice: "",
  avoidPhrases: "",
  extraContext: "",
  language: "en",
};

const toneOptions = [
  "Persuasive",
  "Bold",
  "Professional",
  "Confident",
  "Warm",
  "Conversational",
  "Direct",
];

const languageOptions = [
  { value: "en", labelKey: "english", fallback: "English" },
  { value: "es", labelKey: "spanish", fallback: "Spanish" },
  { value: "fr", labelKey: "french", fallback: "French" },
  { value: "pt", labelKey: "portuguese", fallback: "Portuguese" },
];

const platformSuggestions = [
  "Facebook",
  "Instagram",
  "LinkedIn",
  "X",
  "YouTube",
];

const AdCopyTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const [form, setForm] = useState(emptyForm);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      panelBg: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
      panelBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      panelShadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.30)"
        : "0 10px 30px rgba(15, 23, 42, 0.06)",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputBorder: isDark ? "rgba(148,163,184,0.24)" : "#d1d5db",
      inputText: isDark ? "#f8fafc" : "#111827",
      title: isDark ? "#f8fafc" : "#0f172a",
      text: isDark ? "#e2e8f0" : "#111827",
      muted: isDark ? "#94a3b8" : "#64748b",
      subCardBg: isDark ? "rgba(15,23,42,0.88)" : "#f8fafc",
      subCardBorder: isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb",
      chipBg: isDark ? "rgba(30,41,59,0.95)" : "#eef2ff",
      chipText: isDark ? "#cbd5e1" : "#4338ca",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      errorBg: isDark ? "rgba(127,29,29,0.22)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.32)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      successBg: "#16a34a",
      headlineBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      headlineBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      headlineText: isDark ? "#bfdbfe" : "#1d4ed8",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "11px 12px",
    borderRadius: "12px",
    border: `1px solid ${colors.inputBorder}`,
    background: colors.inputBg,
    color: colors.inputText,
    boxSizing: "border-box",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    color: colors.text,
    fontWeight: "700",
    fontSize: "14px",
  };

  const helperStyle = {
    color: colors.muted,
    fontSize: "12px",
    lineHeight: "1.5",
    marginTop: "6px",
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const copyText = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1800);
    } catch (err) {
      console.error("Failed to copy ad copy:", err);
      setError(t("studioTools.adCopyTool.messages.copyFailed"));
    }
  };

  const buildFullAdText = (item) => {
    return [
      item.primary_text ? item.primary_text : "",
      item.headline
        ? `${t("studioTools.adCopyTool.output.headline")}: ${item.headline}`
        : "",
      item.description
        ? `${t("studioTools.adCopyTool.output.description")}: ${item.description}`
        : "",
      item.cta ? `${t("studioTools.adCopyTool.output.cta")}: ${item.cta}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");
  };

  const handleGenerate = async () => {
    if (!sharedTopic.trim()) {
      setError(t("studioTools.adCopyTool.messages.topicRequired"));
      return;
    }

    try {
      setLoading(true);
      setError("");
      setAds([]);

      const response = await API.post("/studio/ad-copy", {
        topic: sharedTopic,
        platform: sharedPlatform || "Facebook",
        audience: form.audience,
        goal: form.goal,
        offer: form.offer,
        pain_point: form.painPoint,
        desired_outcome: form.desiredOutcome,
        angle: form.angle,
        call_to_action: form.callToAction,
        tone: form.tone,
        keywords: form.keywords,
        brand_voice: form.brandVoice,
        avoid_phrases: form.avoidPhrases,
        extra_context: form.extraContext,
        language: form.language,
      });

      setAds(response?.data?.ads || []);
    } catch (err) {
      console.error("Failed to generate ad copy:", err);
      setError(
        err?.response?.data?.detail || t("studioTools.adCopyTool.messages.generateFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(emptyForm);
    setAds([]);
    setError("");
    setCopiedIndex(null);
  };

  return (
    <MotionSurface>
    <div
      style={{
        display: "grid",
        gap: "18px",
      }}
    >
      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          border: `1px solid ${colors.panelBorder}`,
          background: colors.panelBg,
          boxShadow: colors.panelShadow,
        }}
      >
        <div style={{ marginBottom: "18px" }}>
          <h2
            style={{
              margin: 0,
              color: colors.title,
              fontSize: "22px",
              lineHeight: "1.15",
            }}
          >
            {t("studioTools.adCopyTool.title")}
          </h2>
          <p
            style={{
              margin: "8px 0 0",
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.adCopyTool.description")}
          </p>
        </div>

        {error ? (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px 14px",
              borderRadius: "12px",
              border: `1px solid ${colors.errorBorder}`,
              background: colors.errorBg,
              color: colors.errorText,
              lineHeight: "1.55",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.topic.label")}</label>
            <input
              type="text"
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.topic.placeholder")}
              style={inputStyle}
            />
            <div style={helperStyle}>
              {t("studioTools.adCopyTool.fields.topic.help")}
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.platform.label")}</label>
            <input
              list="ad-copy-platforms"
              type="text"
              value={sharedPlatform}
              onChange={(e) => setSharedPlatform(e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.platform.placeholder")}
              style={inputStyle}
            />
            <datalist id="ad-copy-platforms">
              {platformSuggestions.map((item) => (
                <option key={item} value={item} />
              ))}
            </datalist>
            <div style={helperStyle}>
              {t("studioTools.adCopyTool.fields.platform.help")}
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.tone.label")}</label>
            <select
              value={form.tone}
              onChange={(e) => handleChange("tone", e.target.value)}
              style={inputStyle}
            >
              {toneOptions.map((tone) => (
                <option key={tone} value={tone}>
                  {t(`studioTools.adCopyTool.toneOptions.${tone}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.language.label")}</label>
            <select
              value={form.language}
              onChange={(e) => handleChange("language", e.target.value)}
              style={inputStyle}
            >
              {languageOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(`studioTools.adCopyTool.languageOptions.${item.labelKey}`, item.fallback)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.audience.label")}</label>
            <input
              type="text"
              value={form.audience}
              onChange={(e) => handleChange("audience", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.audience.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.goal.label")}</label>
            <input
              type="text"
              value={form.goal}
              onChange={(e) => handleChange("goal", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.goal.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.offer.label")}</label>
            <input
              type="text"
              value={form.offer}
              onChange={(e) => handleChange("offer", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.offer.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.painPoint.label")}</label>
            <input
              type="text"
              value={form.painPoint}
              onChange={(e) => handleChange("painPoint", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.painPoint.placeholder")}
              style={inputStyle}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>
              {t("studioTools.adCopyTool.fields.desiredOutcome.label")}
            </label>
            <input
              type="text"
              value={form.desiredOutcome}
              onChange={(e) => handleChange("desiredOutcome", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.desiredOutcome.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.angle.label")}</label>
            <input
              type="text"
              value={form.angle}
              onChange={(e) => handleChange("angle", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.angle.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.adCopyTool.fields.callToAction.label")}
            </label>
            <input
              type="text"
              value={form.callToAction}
              onChange={(e) => handleChange("callToAction", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.callToAction.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.adCopyTool.fields.brandVoice.label")}
            </label>
            <input
              type="text"
              value={form.brandVoice}
              onChange={(e) => handleChange("brandVoice", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.brandVoice.placeholder")}
              style={inputStyle}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>{t("studioTools.adCopyTool.fields.keywords.label")}</label>
            <input
              type="text"
              value={form.keywords}
              onChange={(e) => handleChange("keywords", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.keywords.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.adCopyTool.fields.avoidPhrases.label")}
            </label>
            <input
              type="text"
              value={form.avoidPhrases}
              onChange={(e) => handleChange("avoidPhrases", e.target.value)}
              placeholder={t("studioTools.adCopyTool.fields.avoidPhrases.placeholder")}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>
            {t("studioTools.adCopyTool.fields.extraContext.label")}
          </label>
          <textarea
            value={form.extraContext}
            onChange={(e) => handleChange("extraContext", e.target.value)}
            placeholder={t("studioTools.adCopyTool.fields.extraContext.placeholder")}
            rows={4}
            style={{
              ...inputStyle,
              resize: "vertical",
            }}
          />
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.adCopyTool.actions.generating")
              : t("studioTools.adCopyTool.actions.generate")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={handleReset}
            disabled={loading}
          >
            {t("studioTools.adCopyTool.actions.reset")}
          </UtilityButton>
        </ActionRow>
      </div>

      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          border: `1px solid ${colors.panelBorder}`,
          background: colors.panelBg,
          boxShadow: colors.panelShadow,
        }}
      >
        <div style={{ marginBottom: "14px" }}>
          <h3
            style={{
              margin: 0,
              color: colors.title,
              fontSize: "18px",
            }}
          >
            {t("studioTools.adCopyTool.results.title")}
          </h3>
          <p
            style={{
              margin: "8px 0 0",
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.adCopyTool.results.description")}
          </p>
        </div>

        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : ads.length === 0 ? (
          <div
            style={{
              padding: "16px",
              borderRadius: "14px",
              border: `1px solid ${colors.subCardBorder}`,
              background: colors.subCardBg,
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.adCopyTool.results.empty")}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: "14px",
            }}
          >
            {ads.map((item, index) => {
              const fullText = buildFullAdText(item);

              return (
                <ResultCard
                  key={`${item.variant_name || "ad"}-${index}`}
                  colors={{
                    border: colors.subCardBorder,
                    cardBgStrong: colors.subCardBg,
                    shadowSoft: colors.panelShadow,
                  }}
                  index={index}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <h4
                        style={{
                          margin: 0,
                          color: colors.title,
                          fontSize: "16px",
                          lineHeight: "1.3",
                        }}
                      >
                        {item.variant_name ||
                          `${t("studioTools.adCopyTool.results.variantFallback")} ${index + 1}`}
                      </h4>

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          marginTop: "8px",
                        }}
                      >
                        {item.label ? (
                          <span
                            style={{
                              padding: "4px 9px",
                              borderRadius: "999px",
                              background: colors.chipBg,
                              color: colors.chipText,
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            {item.label}
                          </span>
                        ) : null}

                        {typeof item.score === "number" ? (
                          <span
                            style={{
                              padding: "4px 9px",
                              borderRadius: "999px",
                              background: colors.chipBg,
                              color: colors.chipText,
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            {t("studioTools.adCopyTool.results.score")}: {item.score}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <div style={labelStyle}>
                        {t("studioTools.adCopyTool.output.primaryText")}
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.7",
                          fontSize: "14px",
                        }}
                      >
                        {item.primary_text}
                      </div>
                    </div>

                    <div
                      style={{
                        padding: "12px",
                        borderRadius: "12px",
                        background: colors.headlineBg,
                        border: `1px solid ${colors.headlineBorder}`,
                      }}
                    >
                      <div
                        style={{
                          color: colors.headlineText,
                          fontWeight: "700",
                          fontSize: "12px",
                          marginBottom: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {t("studioTools.adCopyTool.output.headline")}
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          fontWeight: "700",
                          fontSize: "15px",
                          lineHeight: "1.5",
                        }}
                      >
                        {item.headline}
                      </div>
                    </div>

                    {item.description ? (
                      <div>
                        <div style={labelStyle}>
                          {t("studioTools.adCopyTool.output.description")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            lineHeight: "1.7",
                            fontSize: "14px",
                          }}
                        >
                          {item.description}
                        </div>
                      </div>
                    ) : null}

                    {item.cta ? (
                      <div>
                        <div style={labelStyle}>{t("studioTools.adCopyTool.output.cta")}</div>
                        <div
                          style={{
                            color: colors.text,
                            lineHeight: "1.7",
                            fontSize: "14px",
                            fontWeight: "700",
                          }}
                        >
                          {item.cta}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <ActionRow style={{ marginTop: "14px" }}>
                    <CopyButton
                      copied={copiedIndex === index}
                      type="button"
                      onClick={() => copyText(fullText, index)}
                    >
                      {copiedIndex === index
                        ? t("studioTools.adCopyTool.actions.copied")
                        : t("studioTools.adCopyTool.actions.copy")}
                    </CopyButton>

                    <SaveStudioAssetButton
                      toolType="ad_copy_generator"
                      title={
                        item.variant_name ||
                        `${t("studioTools.adCopyTool.results.variantFallback")} ${index + 1}`
                      }
                      topic={sharedTopic || item.variant_name || t("studioTools.adCopyTool.title")}
                      platform={sharedPlatform || "Facebook"}
                      content={fullText}
                      meta={{
                        source_tool: "ad_copy_generator",
                        variant_name:
                          item.variant_name ||
                          `${t("studioTools.adCopyTool.results.variantFallback")} ${index + 1}`,
                        headline: item.headline || "",
                        description: item.description || "",
                        cta: item.cta || "",
                        score: item.score,
                        label: item.label,
                      }}
                    />
                  </ActionRow>
                </ResultCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default AdCopyTool;